import { collectionStateText } from './collectionModel'
import { previewInventoryMessage } from './previewInventory'
import { useQuery } from '@tanstack/react-query'
import { nativeSportsbookApi } from '@/services/api'
import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useDrag, useDrop } from 'react-dnd'
import { ArrowUpRight, Search, BarChart3, ChevronRight, CircleUserRound, History, Home, LandPlot, Layers, LayoutGrid, Moon, Play, ShieldCheck, Sparkles, Sun, Trophy, Wallet, Wifi, Zap } from 'lucide-react'
import { HOME_CARD_DRAG_TYPE, HOME_COMPONENT_DRAG_TYPE, HOME_LIBRARY_DRAG_TYPE, dropEdge, dropIndex } from './nativeDnd'
import { revealPreviewElement } from './nativePreviewScroll'
import { cardKindDefinition, cardName, componentDefinition, componentInstanceName, componentName, routeLabel } from './nativeSportsbookManifest'
import {
  ALL_SPORTS_ID,
  PREVIEW_CATALOG,
  QUICK_FILTERS,
  sportDisplayName,
  activeCards,
  americanOdds,
  brandMonogram,
  cardScheduleState,
  catalogSports,
  clockDisplay,
  combinedOddsMillis,
  competitorNames,
  eventCardMarketColumns,
  eventMatchesManifestFilter,
  eventMatchesQuickFilter,
  firstLiveEvent,
  formatStartTime,
  homeMomentum,
  oddsCaption,
  outcomeReference,
  parlayLegs,
  previewFontVariables,
  previewThemeVariables,
  resolveLiveGameEvent,
  resolvePreviewTheme,
  resolveSpotlightEvent,
  selectedSportEventSection,
} from './nativePreviewModel'
import { navigationGlyph, resolveHomeNavigation } from './homeNavigationModel'
import { displayAccent, eventTeamColors, isSGPEligible } from './nativePreviewTeamColors'
import competitionMarketsBundle from '../../../../contracts/native-sportsbook/v1/competition-markets.json'
import { PlayerPropBoardPreview } from './PlayerPropBoardPreview'
import { ParlayBuilderPreview } from './ParlayBuilderPreview'
import styles from './NativePreview.module.css'
import { isLiveSource } from './catalogSources'

// The fixture player the simulator signs in as (FixturePlayerAdapter wallet).
const FIXTURE_WALLET = '$500.00'

// iPhone 16 logical screen (393×852 pt) plus the drawn bezel.
export const DEVICE_OUTER_WIDTH = 417
export const DEVICE_OUTER_HEIGHT = 876
// Space kept clear below the stage for the sticky status bar; the floating
// release dock may overlap the phone's bottom edge until the page is scrolled.
const STAGE_RESERVED_BELOW = 72
const STAGE_STICKY_TOP = 16

function scrollParentOf(element) {
  let node = element?.parentElement
  while (node) {
    const { overflowY } = window.getComputedStyle(node)
    if (overflowY === 'auto' || overflowY === 'scroll') return node
    node = node.parentElement
  }
  return null
}

/**
 * Keeps the device at real phone proportions and scales it down only when the
 * column cannot show a full phone: by height (viewport minus the sticky offset
 * and the dock/status reserve) and by width (narrow layouts scroll normally). Measured with a
 * ResizeObserver so the scale follows window and layout changes.
 */
export function DeviceStage({ children }) {
  const stageRef = useRef(null)
  useLayoutEffect(() => {
    const stage = stageRef.current
    if (!stage) return undefined
    const scroller = scrollParentOf(stage)
    const column = stage.parentElement
    const fit = () => {
      const viewportBottom = scroller ? Math.min(window.innerHeight, scroller.getBoundingClientRect().bottom) : window.innerHeight
      const availableHeight = viewportBottom - Math.max(STAGE_STICKY_TOP, stage.getBoundingClientRect().top) - STAGE_RESERVED_BELOW
      const availableWidth = column?.clientWidth || 0
      const stacked = window.matchMedia('(max-width: 64rem)').matches
      const byHeight = !stacked && availableHeight > 0 ? availableHeight / DEVICE_OUTER_HEIGHT : 1
      const byWidth = availableWidth > 0 ? availableWidth / DEVICE_OUTER_WIDTH : 1
      const scale = Math.min(1, byHeight, byWidth)
      stage.style.setProperty('--np-device-scale', scale.toFixed(4))
    }
    fit()
    if (typeof ResizeObserver === 'undefined') return undefined
    const observer = new ResizeObserver(fit)
    if (scroller) observer.observe(scroller)
    if (column) observer.observe(column)
    window.addEventListener('resize', fit)
    return () => { observer.disconnect(); window.removeEventListener('resize', fit) }
  }, [])
  return (
    <div ref={stageRef} className={styles.stage} data-testid="device-stage">
      <div className={styles.stageInner}>{children}</div>
    </div>
  )
}

export function DeviceChrome() {
  return (
    <div className={styles.chrome} aria-hidden="true">
      <span>9:41</span>
      <span className={styles.island} />
      <span className={styles.chromeIcons}>
        <span className={styles.signal}><i /><i /><i /><i /></span>
        <Wifi size={14} strokeWidth={2.5} />
        <span className={styles.battery} />
      </span>
    </div>
  )
}

function AppHeader({ theme, authenticated, onToggleAppearance }) {
  return (
    <div className={styles.header}>
      <div className={styles.brand}>
        <span className={styles.monogram}>{brandMonogram(theme.name)}</span>
        <span className={styles.brandText}>
          <span className={styles.brandName}>{theme.name}</span>
          <span className={styles.brandMeta}>US-NJ • DGE Regulated</span>
        </span>
      </div>
      <div className={styles.headerActions}>
        {onToggleAppearance ? (
          <button type="button" className={styles.circleButton} onClick={onToggleAppearance} aria-label={theme.appearance === 'dark' ? 'Switch preview to light mode' : 'Switch preview to dark mode'}>
            {theme.appearance === 'dark' ? <Sun size={12} strokeWidth={3} /> : <Moon size={12} strokeWidth={3} />}
          </button>
        ) : null}
        <span className={styles.circleButton} aria-hidden="true"><History size={13} strokeWidth={3} /></span>
        {authenticated ? (
          <>
            <span className={styles.circleButton} aria-hidden="true"><CircleUserRound size={13} strokeWidth={3} /></span>
            <span className={styles.walletPill}><Wallet size={10} />{FIXTURE_WALLET}</span>
          </>
        ) : <span className={styles.loginPill}>LOG IN</span>}
      </div>
    </div>
  )
}

export function SportSymbol({ symbol, size = 12 }) {
  switch (symbol) {
    case 'house.fill':
      return <Home size={size} strokeWidth={2.5} fill="currentColor" />
    case 'square.grid.2x2.fill':
      return <LayoutGrid size={size} strokeWidth={2.5} />
    case 'bolt.horizontal.fill':
      return <Zap size={size} fill="currentColor" strokeWidth={0} />
    case 'football.fill':
      return (
        <svg width={size} height={size} viewBox="0 0 16 16" fill="currentColor">
          <ellipse cx="8" cy="8" rx="6.8" ry="4.4" transform="rotate(-45 8 8)" />
          <path d="M5.5 10.5 L10.5 5.5 M7 7 L9 9 M6 8 L8 10 M8 6 L10 8" stroke="var(--np-bg, #080A0F)" strokeWidth="0.9" strokeLinecap="round" />
        </svg>
      )
    case 'soccerball':
      return (
        <svg width={size} height={size} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.1">
          <circle cx="8" cy="8" r="6.8" />
          <polygon points="8,5.2 10.2,6.8 9.4,9.2 6.6,9.2 5.8,6.8" fill="currentColor" stroke="none" />
          <line x1="8" y1="5.2" x2="8" y2="1.2" />
          <line x1="10.2" y1="6.8" x2="14.2" y2="5.5" />
          <line x1="9.4" y1="9.2" x2="12.5" y2="13.2" />
          <line x1="6.6" y1="9.2" x2="3.5" y2="13.2" />
          <line x1="5.8" y1="6.8" x2="1.8" y2="5.5" />
        </svg>
      )
    case 'baseball.fill':
      return (
        <svg width={size} height={size} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.1">
          <circle cx="8" cy="8" r="6.8" />
          <path d="M4.5 2.5 C6.2 5.5 6.2 10.5 4.5 13.5" strokeDasharray="1.2 0.8" />
          <path d="M11.5 2.5 C9.8 5.5 9.8 10.5 11.5 13.5" strokeDasharray="1.2 0.8" />
        </svg>
      )
    case 'basketball.fill':
      return (
        <svg width={size} height={size} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.1">
          <circle cx="8" cy="8" r="6.8" />
          <line x1="1.2" y1="8" x2="14.8" y2="8" />
          <line x1="8" y1="1.2" x2="8" y2="14.8" />
          <path d="M3.2 3.2 C5.8 5.8 5.8 10.2 3.2 12.8" />
          <path d="M12.8 3.2 C10.2 5.8 10.2 10.2 12.8 12.8" />
        </svg>
      )
    case 'hockey.puck.fill':
      return (
        <svg width={size} height={size} viewBox="0 0 16 16" fill="currentColor">
          <path d="M1.5 6 C1.5 4.2 4.4 2.8 8 2.8 C11.6 2.8 14.5 4.2 14.5 6 L14.5 10 C14.5 11.8 11.6 13.2 8 13.2 C4.4 13.2 1.5 11.8 1.5 10 Z" />
          <ellipse cx="8" cy="6" rx="6.5" ry="3.2" fill="none" stroke="var(--np-bg, #080A0F)" strokeWidth="0.8" />
        </svg>
      )
    case 'tennisball.fill':
      return (
        <svg width={size} height={size} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.1">
          <circle cx="8" cy="8" r="6.8" />
          <path d="M1.5 8 C5.2 8 8 5.2 8 1.5" />
          <path d="M14.5 8 C10.8 8 8 10.8 8 14.5" />
        </svg>
      )
    case 'figure.boxing':
      return (
        <svg width={size} height={size} viewBox="0 0 16 16" fill="currentColor">
          <path d="M4 4 C4 2.5 6 1.5 8.5 1.5 C11 1.5 12.5 2.5 12.5 5 C12.5 7 11.5 8.5 10 9.5 L10 12 C10 12.5 9.5 13 9 13 L7 13 C6.5 13 6 12.5 6 12 L6 10 C4.5 9.5 4 8 4 6.5 Z" />
        </svg>
      )
    default:
      return <Trophy size={size} strokeWidth={2.2} />
  }
}

export function QuickFilterSymbol({ symbol, size = 10 }) {
  switch (symbol) {
    case 'sparkles':
      return <Sparkles size={size} strokeWidth={2.2} />
    case 'bolt.fill':
      return <Zap size={size} fill="currentColor" strokeWidth={0} />
    case 'square.stack.3d.up.fill':
      return <Layers size={size} strokeWidth={2.2} />
    case 'football.fill':
      return <SportSymbol symbol="football.fill" size={size} />
    case 'baseball.fill':
      return <SportSymbol symbol="baseball.fill" size={size} />
    case 'soccerball':
      return <SportSymbol symbol="soccerball" size={size} />
    default:
      return null
  }
}

// SportNavigationBar (AppChrome.swift). Browse Sports and BetWire open screens the
// canvas does not model, so they are drawn as chrome rather than as controls.
function SportNav({ sports, selected, onSelect }) {
  const isHomeSelected = selected === ALL_SPORTS_ID
  return (
    <div className={styles.sportNav} role="group" aria-label="Sport filter">
      <button
        type="button"
        className={styles.sportPill}
        aria-pressed={isHomeSelected}
        onClick={() => onSelect(ALL_SPORTS_ID)}
      >
        <span className={styles.sportIcon} aria-hidden="true">
          <Home size={12} strokeWidth={isHomeSelected ? 2.5 : 2} fill={isHomeSelected ? 'currentColor' : 'none'} />
        </span>
        Home
      </button>

      {sports.filter((sport) => sport.id !== ALL_SPORTS_ID).map((sport) => {
        const isSelected = sport.id === selected
        return (
          <button
            key={sport.id}
            type="button"
            className={styles.sportPill}
            aria-pressed={isSelected}
            onClick={() => onSelect(sport.id)}
          >
            <span className={styles.sportIcon} aria-hidden="true">
              <SportSymbol symbol={sport.symbol} size={12} />
            </span>
            {sport.name}
            {sport.liveCount > 0 ? (
              <span className={styles.liveCount}>
                <i />
                {sport.liveCount}
              </span>
            ) : null}
          </button>
        )
      })}

      <span className={styles.sportPill}>
        <span className={styles.sportIcon}><LayoutGrid size={12} strokeWidth={2} /></span>
        Browse Sports
      </span>

      <span className={styles.sportPill}>
        <span className={styles.sportIcon}><Zap size={12} fill="currentColor" strokeWidth={0} /></span>
        BetWire
      </span>
    </div>
  )
}

function QuickFilters({ active, onSelect }) {
  return (
    <div className={styles.filters} role="group" aria-label="Quick filters">
      {QUICK_FILTERS.map(({ id, label, symbol }) => {
        const isSelected = active === id
        return (
          <button
            key={id}
            type="button"
            className={styles.filterChip}
            aria-pressed={isSelected}
            onClick={() => onSelect(isSelected ? '' : id)}
          >
            <span className={styles.filterIcon} aria-hidden="true">
              <QuickFilterSymbol symbol={symbol} size={10} />
            </span>
            {label}
          </button>
        )
      })}
    </div>
  )
}

function ManagedNavigation({ bar, options, selected, label, onSelect, onEdit, editing, trailingActions }) {
  if (!bar.enabled || !options.length) return null
  return <div className={styles.managedNavigation} data-style={bar.style} data-emphasis={bar.emphasis} data-editing={editing} role="group" aria-label={label}>
    {options.map((item) => <button key={item.id} type="button" className={styles.managedChoice} aria-pressed={item.id === selected?.id} onClick={() => onSelect(item.id)} title={`${item.count} events · ${item.liveCount} in-play`}>
      <span className={styles.managedChoiceSurface}>
        {bar.show_icons ? <span className={styles.managedGlyph} aria-hidden="true">{navigationGlyph(item)}</span> : null}
        <span>{item.label}{bar.show_counts ? <small>{item.count}</small> : null}</span>
      </span>
    </button>)}
    {trailingActions ? <>
      <span className={styles.managedDivider} aria-hidden="true" />
      <button
        type="button"
        tabIndex={-1}
        className={styles.managedAction}
        aria-label="Open All Sports"
        data-testid="home-nav-all-sports"
      >
        <span className={styles.managedActionSurface}>
          <span className={styles.managedActionIcon} aria-hidden="true">
            <LandPlot size={12} strokeWidth={2} />
          </span>
          <span>All Sports</span>
        </span>
      </button>
      <button
        type="button"
        tabIndex={-1}
        className={styles.managedAction}
        aria-label="Open BetWire"
        data-testid="home-nav-betwire"
      >
        <span className={styles.managedActionSurface}>
          <span className={styles.managedActionIcon} aria-hidden="true">
            <Zap size={12} fill="currentColor" strokeWidth={0} />
          </span>
          <span>BetWire</span>
        </span>
      </button>
    </> : null}
    {onEdit ? <button type="button" className={styles.editNavigation} onClick={onEdit} aria-label={`Edit ${label}`}>Edit</button> : null}
  </div>
}

export function TeamBadge({ participant, size = 38 }) {
  const presentation = participant?.presentation
  if (!presentation || presentation.kind === 'none' || !presentation.primary_color || !presentation.secondary_color) return null
  const gradient = `linear-gradient(225deg, ${presentation.secondary_color} 0%, ${presentation.secondary_color} 49%, ${presentation.primary_color} 51%, ${presentation.primary_color} 100%)`
  return <span className={styles.teamBadge} style={{ width: size, height: size, background: gradient }} aria-hidden="true" />
}

export function OddsCell({ outcome, marketType, marketStatus, context, pricesAvailable = true }) {
  const open = pricesAvailable && outcome.status === 'OPEN' && marketStatus === 'OPEN' && outcome.odds != null
  const suspended = outcome.status === 'SUSPENDED' || marketStatus === 'SUSPENDED'
  const caption = oddsCaption(outcome, marketType, context)
  return (
    <span className={styles.odds} data-open={open ? 'true' : 'false'}>
      {caption ? <span className={styles.oddsCaption}>{caption}</span> : null}
      {suspended ? <span className={styles.susp}>SUSP</span> : <span className={styles.oddsValue}>{americanOdds(outcome.odds)}</span>}
    </span>
  )
}

function MomentumMeter({ home, away, momentum, awayTint, homeTint }) {
  const awayColor = awayTint || 'var(--np-negative)'
  const homeColor = homeTint || 'var(--np-positive)'
  const awayWidth = Math.min(Math.max((1 - momentum) * 100, 5), 95)
  const homeWidth = 100 - awayWidth

  return (
    <div className={styles.momentum}>
      <div className={styles.momentumLabels}>
        <span>{away}</span>
        <span className={styles.momentumTag}><Zap size={8} fill="currentColor" />MOMENTUM</span>
        <span>{home}</span>
      </div>
      <div className={styles.momentumTrack}>
        <span
          className={styles.momentumAway}
          style={{
            width: `${awayWidth}%`,
            background: `linear-gradient(90deg, ${awayColor}, color-mix(in srgb, ${awayColor} 55%, transparent))`,
          }}
        />
        <span
          className={styles.momentumSeam}
          style={{ left: `${awayWidth}%` }}
          aria-hidden="true"
        />
        <span
          className={styles.momentumHome}
          style={{
            width: `${homeWidth}%`,
            background: `linear-gradient(90deg, color-mix(in srgb, ${homeColor} 55%, transparent), ${homeColor})`,
          }}
        />
      </div>
    </div>
  )
}

function LiveShowcaseCard({ event }) {
  const [away, home] = competitorNames(event)
  const awayParticipant = event.participants?.find((participant) => participant.role === 'away')
  const homeParticipant = event.participants?.find((participant) => participant.role === 'home')
  const { away: awayAccent, home: homeAccent } = eventTeamColors(event)
  const occurrence = event.score?.match_occurrence
  const caption = occurrence || (event.score ? null : 'Awaiting first score')
  const primaryMarket = event.markets?.find((market) => market.type === 'MONEYLINE' && market.outcomes.length <= 3) || event.markets?.find((market) => market.outcomes.length <= 3)
  return (
    <div className={`${styles.card} ${styles.liveCard}`}>
      <div className={styles.liveHeader}>
        {event.upcoming ? (
          <span className={styles.upcomingPill}>
            {`Upcoming · ${formatStartTime(event.start_time)}`}
          </span>
        ) : (
          <span className={styles.livePill}>
            <span className={styles.beacon} />
            LIVE IN-PLAY
            {event.clock ? <span className={styles.liveClock}>•  {clockDisplay(event.clock)}</span> : null}
          </span>
        )}
        <span className={styles.tvPill}>{event.league}</span>
      </div>
      <div className={styles.scoreboard}>
        <div className={styles.team}><TeamBadge participant={awayParticipant} /><span className={styles.teamName}>{away}</span></div>
        <div className={styles.score}>
          <span className={styles.scoreValue}>{event.score ? `${event.score.away_score} - ${event.score.home_score}` : '—'}</span>
          {caption ? <span className={styles.scoreCaption} data-occurrence={occurrence ? 'true' : 'false'}>{caption}</span> : null}
        </div>
        <div className={styles.team}><TeamBadge participant={homeParticipant} /><span className={styles.teamName}>{home}</span></div>
      </div>
      {event.score ? <MomentumMeter home={home} away={away} momentum={homeMomentum(event.score)} awayTint={awayAccent} homeTint={homeAccent} /> : null}
      {primaryMarket ? (
        <div className={styles.oddsStrip}>
          {primaryMarket.outcomes.slice(0, 3).map((outcome) => <OddsCell key={outcome.id} outcome={outcome} marketType={primaryMarket.type} marketStatus={primaryMarket.status} context="standalone" />)}
        </div>
      ) : null}
    </div>
  )
}

function PromoHero({ component }) {
  const actionLabel = component.action?.label || (component.action?.route ? routeLabel(component.action.route) : null)
  return (
    <div className={`${styles.card} ${styles.promoHero}`}>
      <span className={styles.promoHeadline}>{component.headline}</span>
      <span className={styles.promoBody}>{component.body}</span>
      {actionLabel ? <span className={styles.promoAction}>{actionLabel}</span> : null}
    </div>
  )
}

function EventCard({ event, configuration, pricesAvailable = true }) {
  const columns = eventCardMarketColumns(event, configuration)
  const fullDetails = configuration.metadata_style !== 'minimal'
  const [away, home] = competitorNames(event)
  const awayParticipant = event.participants?.find((participant) => participant.role === 'away')
  const homeParticipant = event.participants?.find((participant) => participant.role === 'home')
  const sgpEligible = isSGPEligible(event)
  const { away: awayAccent, home: homeAccent } = eventTeamColors(event)
  const topColor = awayAccent || homeAccent
  const bottomColor = homeAccent || awayAccent
  const hasTeamColors = Boolean(topColor || bottomColor)

  return (
    <div className={`${styles.card} ${styles.eventCard}`} data-card-density={configuration.card_density || 'comfortable'}>
      {hasTeamColors ? (
        <div
          className={styles.teamRail}
          style={{
            '--top-color': topColor,
            '--bottom-color': bottomColor,
          }}
          aria-hidden="true"
        />
      ) : null}
      <div className={styles.cardHeader}>
        <div className={styles.cardHeaderCluster}>
          {fullDetails ? <span className={styles.leagueTag}>{event.league}</span> : null}
          {sgpEligible ? <span className={styles.sgpTag}>SGP</span> : null}
          {event.live ? <span className={styles.liveTag}><i />LIVE</span> : <span className={styles.startTime}>{formatStartTime(event.start_time)}</span>}
        </div>
        <span className={styles.marketsPill}>{event.markets.length} MARKETS <ChevronRight size={9} strokeWidth={3} /></span>
      </div>
      <div className={styles.cardBody}>
        <div className={styles.teams}>
          <div className={styles.teamsSpacer} />
          <div className={styles.teamRow}><TeamBadge participant={awayParticipant} size={24} /><span>{away}</span></div>
          <div className={styles.teamRow}><TeamBadge participant={homeParticipant} size={24} /><span>{home}</span></div>
        </div>
        <div className={styles.columns} data-dense={columns.length > 3 ? 'true' : 'false'}>
          {columns.map((column) => (
            <div key={column.id} className={styles.column}>
              <span className={styles.columnTitle}>{column.title}</span>
              <div className={styles.columnCells} data-single={column.outcomes.length === 1 ? 'true' : 'false'}>
                {column.outcomes.map((outcome) => <OddsCell key={outcome.id} outcome={outcome} marketType={column.captionType} marketStatus={column.market.status} context="grid" pricesAvailable={pricesAvailable} />)}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

function EventListSection({ title, events, configuration, cardConfigurations, message, pricesAvailable = true }) {
  return (
    <div className={styles.eventList}>
      <div className={styles.sectionHeader}><span className={styles.sectionTitle}>{title}</span><span className={styles.sectionCount}>{events.length} {events.length === 1 ? 'Game' : 'Games'}</span></div>
      {message ? <div className={styles.navigationEmpty}><span>{message}</span></div> : null}
      {events.map((event) => <EventCard key={event.id} event={event} pricesAvailable={pricesAvailable} configuration={cardConfigurations?.get(event.id) || configuration} />)}
    </div>
  )
}

function ComplianceNotice({ component }) {
  return (
    <div className={`${styles.card} ${styles.compliance}`}>
      <ShieldCheck size={20} strokeWidth={2.5} className={styles.shield} />
      <span className={styles.complianceText}>
        <span className={styles.complianceLabel}>RESPONSIBLE GAMING</span>
        <span className={styles.complianceBody}>{component.text}</span>
      </span>
      <span className={styles.agePill}>21+</span>
    </div>
  )
}

function EditorPlaceholder({ children }) {
  return <div className={styles.placeholder}>{children}</div>
}

function PromotionCard({ card }) {
  const style = card.accent ? { '--np-feature-accent': card.accent } : undefined
  return (
    <div className={`${styles.featureCard} ${styles.promotionCard}`} style={style} data-image={card.image_url ? 'true' : 'false'}>
      {card.image_url ? <img className={styles.promotionImage} src={card.image_url} alt="" /> : null}
      <div className={styles.promotionBody}>
        {card.badge ? <span className={styles.featureBadge}>{card.badge}</span> : null}
        <span className={styles.promotionHeadline}>{card.headline}</span>
        {card.body ? <span className={styles.promotionText}>{card.body}</span> : null}
        <span className={styles.promotionAction}>{card.action?.label || (card.action?.route ? routeLabel(card.action.route) : null)}</span>
        {card.terms ? <span className={styles.promotionTerms}>{card.terms}</span> : null}
      </div>
    </div>
  )
}

function EditorialSpotlight({ event, headline, actionLabel }) {
  const [away, home] = competitorNames(event)
  const awayParticipant = event.participants?.find((participant) => participant.role === 'away')
  const homeParticipant = event.participants?.find((participant) => participant.role === 'home')
  const market = event.markets.find((candidate) => candidate.type === 'MONEYLINE' && candidate.outcomes.length <= 3) || event.markets.find((candidate) => candidate.outcomes.length <= 3)
  return (
    <div className={`${styles.featureCard} ${styles.spotlightCard}`}>
      <div className={styles.cardHeader}>
        <span className={styles.leagueTag}>{event.league}</span>
        <span className={styles.startTime}>{event.live ? 'IN-PLAY' : formatStartTime(event.start_time)}</span>
        <span className={styles.spotlightEyebrow}>SPOTLIGHT</span>
      </div>
      <h3 className={styles.editorialHeadline}>{headline || 'The matchup to watch'}</h3>
      <div className={styles.scoreboard}>
        <div className={styles.team}><TeamBadge participant={awayParticipant} /><span className={styles.teamName}>{away}</span></div>
        <span className={styles.spotlightAt}>VS</span>
        <div className={styles.team}><TeamBadge participant={homeParticipant} /><span className={styles.teamName}>{home}</span></div>
      </div>
      {market ? (
        <div className={styles.oddsStrip}>
          {market.outcomes.slice(0, 3).map((outcome) => <OddsCell key={outcome.id} outcome={outcome} marketType={market.type} marketStatus={market.status} context="standalone" />)}
        </div>
      ) : null}
      {actionLabel ? <span className={styles.featureLink}>{actionLabel}<ChevronRight size={10} strokeWidth={3} /></span> : null}
    </div>
  )
}

function EventSpotlightCard({ card, catalog, interactive }) {
  const event = resolveSpotlightEvent(card, catalog)
  if (!event) return interactive ? <EditorPlaceholder>No event matches this spotlight. The app skips this card.</EditorPlaceholder> : null
  return <EditorialSpotlight event={event} headline={card.headline} actionLabel={card.action_label || 'Explore matchup'} />
}

function VideoCard({ card }) {
  return (
    <div className={`${styles.featureCard} ${styles.videoCard}`}>
      <div className={styles.videoFrame}>
        {card.video_url ? <video src={card.video_url} poster={card.poster_url || undefined} muted playsInline loop autoPlay={card.autoplay !== false} preload="metadata" /> : (card.poster_url ? <img src={card.poster_url} alt="" /> : null)}
        <span className={styles.playBadge}><Play size={14} fill="currentColor" /></span>
      </div>
      <div className={styles.videoText}>
        <span className={styles.featureHeadline}>{card.headline}</span>
        {card.caption ? <span className={styles.featureCaption}>{card.caption}</span> : null}
        {card.action?.label || card.action?.route ? <span className={styles.featureLink}>{card.action?.label || routeLabel(card.action.route)}<ChevronRight size={10} strokeWidth={3} /></span> : null}
      </div>
    </div>
  )
}

function OddsBoostCard({ card, catalog, interactive }) {
  const reference = outcomeReference(catalog, card)
  if (!reference) return interactive ? <EditorPlaceholder>Choose a selection to boost. The app skips this card.</EditorPlaceholder> : null
  const { event, market, outcome } = reference
  return (
    <div className={`${styles.featureCard} ${styles.boostCard}`}>
      <span className={styles.featureTag}><Zap size={9} fill="currentColor" />ODDS BOOST</span>
      <span className={styles.featureHeadline}>{card.headline}</span>
      <span className={styles.featureCaption}>{event.league} · {event.name}</span>
      <div className={styles.boostRow}>
        <span className={styles.boostSelection}><strong>{outcome.label}</strong><small>{market.label}</small></span>
        <span className={styles.boostPrices}><s>{americanOdds(outcome.odds)}</s><b>{americanOdds(card.boosted_odds)}</b></span>
      </div>
      {card.body ? <span className={styles.featureCaption}>{card.body}</span> : null}
      <span className={styles.featureAction}>Add to betslip</span>
    </div>
  )
}

export function ParlayCard({ card, catalog, interactive, showEvent = true }) {
  const legs = parlayLegs(card, catalog)
  if (legs.length < 2) {
    const configured = card.legs || []
    const reason = configured.length < 2 ? 'Add at least two legs.' : configured.some((leg) => !leg.event_id) ? 'Choose a game for every leg.' : 'A leg no longer matches the catalog.'
    return interactive ? <EditorPlaceholder>{reason} The app skips this card.</EditorPlaceholder> : null
  }
  const combined = combinedOddsMillis(legs.map((leg) => leg.outcome.odds))
  // The ticket stub only claims "same game" when every leg really is from one event; the
  // contract also allows cross-event suggestions, which the apps price as a plain parlay.
  const sameGame = legs.every((leg) => leg.event.id === legs[0].event.id)
  return (
    <div className={`${styles.featureCard} ${styles.sgpTicketCard}`}>
      <div className={styles.sgpStub}>
        <div className={styles.sgpStubLeft}>
          <span className={styles.sgpStubTag}>{sameGame ? 'SAME GAME PARLAY' : 'PARLAY'}</span>
          <span className={styles.sgpStubLegs}>{legs.length} LEGS</span>
        </div>
        <span className={styles.sgpStubPrice}>{americanOdds(combined)}</span>
      </div>
      <div className={styles.sgpPerforation} aria-hidden="true" />
      <div className={styles.sgpCardBody}>
        {card.headline ? <span className={styles.sgpHeadline}>{card.headline}</span> : null}
        <div className={styles.sgpRailList}>
          {legs.map(({ event, market, outcome }, index) => {
            const participant = event.participants?.find((p) => p.name === outcome.label)
            const legColor = displayAccent(participant) || 'var(--np-accent)'
            const isFirst = index === 0
            const isLast = index === legs.length - 1

            return (
              <div key={outcome.id} className={styles.sgpRailItem}>
                <div className={styles.sgpStopColumn} aria-hidden="true">
                  {!isFirst ? <span className={styles.sgpRailSegTop} /> : null}
                  <span
                    className={styles.sgpStopDot}
                    style={{ '--stop-color': legColor }}
                  />
                  {!isLast ? <span className={styles.sgpRailSegBottom} /> : null}
                </div>
                <div className={styles.sgpLegDetails}>
                  <strong className={styles.sgpLegOutcome}>{outcome.label}</strong>
                  <small className={styles.sgpLegMarket}>{showEvent ? `${market.label} · ${event.name}` : market.label}</small>
                </div>
                <b className={styles.sgpLegPrice}>{americanOdds(outcome.odds)}</b>
              </div>
            )
          })}
        </div>
        <div className={styles.sgpActionCapsule}>{legs.length === 2 ? 'Add both to betslip' : `Add all ${legs.length} to betslip`}</div>
      </div>
    </div>
  )
}

function FeaturedCardBody({ card, catalog, interactive }) {
  switch (card.kind) {
    case 'promotion': return <PromotionCard card={card} />
    case 'event_spotlight': return <EventSpotlightCard card={card} catalog={catalog} interactive={interactive} />
    case 'video': return <VideoCard card={card} />
    case 'odds_boost': return <OddsBoostCard card={card} catalog={catalog} interactive={interactive} />
    case 'parlay_suggestion': return <ParlayCard card={card} catalog={catalog} interactive={interactive} />
    default: return null
  }
}

function CardSlot({ component, card, index, axis, selected, interactive, onSelect, onMoveTo, children }) {
  const ref = useRef(null)
  const pointer = useRef(null)
  const [edge, setEdge] = useState(null)
  const schedule = cardScheduleState(card)
  const [{ isDragging }, drag] = useDrag(() => ({
    type: HOME_CARD_DRAG_TYPE,
    item: { componentId: component.id, index },
    canDrag: interactive,
    collect: (monitor) => ({ isDragging: monitor.isDragging() }),
  }), [interactive, component.id, index])
  // Cards only show mostly off-screen in a carousel, so the drop lands on the
  // side of the hovered card's visible part and moves on release rather than
  // reordering live under the pointer.
  const [{ isOver }, drop] = useDrop(() => ({
    accept: HOME_CARD_DRAG_TYPE,
    canDrop: (item) => interactive && item.componentId === component.id && item.index !== index,
    hover: (item, monitor) => {
      if (monitor.canDrop()) setEdge(dropEdge(ref.current, monitor, axis, pointer.current))
    },
    drop: (item, monitor) => {
      const side = dropEdge(ref.current, monitor, axis, pointer.current)
      if (side) onMoveTo?.(component.id, item.index, dropIndex(item.index, index, side))
    },
    collect: (monitor) => ({ isOver: monitor.isOver({ shallow: true }) && monitor.canDrop() }),
  }), [interactive, component.id, index, axis, onMoveTo])
  useEffect(() => {
    if (!isOver) setEdge(null)
  }, [isOver])
  useEffect(() => {
    if (selected) revealPreviewElement(ref.current)
  }, [selected, index])
  drag(drop(ref))
  const select = (event) => { event.stopPropagation(); onSelect?.(component.id, card.id) }
  const trackPointer = (event) => { pointer.current = { x: event.clientX, y: event.clientY } }
  const label = `${cardKindDefinition(card.kind)?.display_name || card.kind}: ${cardName(card)}`
  return (
    <div
      ref={ref}
      className={styles.cardSlot}
      onDragEnter={interactive ? trackPointer : undefined}
      onDragOver={interactive ? trackPointer : undefined}
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      aria-label={interactive ? label : undefined}
      aria-pressed={interactive ? selected : undefined}
      data-interactive={interactive ? 'true' : 'false'}
      data-selected={selected ? 'true' : 'false'}
      data-dragging={isDragging ? 'true' : 'false'}
      data-drop-edge={isOver ? edge : undefined}
      data-schedule={schedule}
      onClick={interactive ? select : undefined}
      onKeyDown={interactive ? (event) => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); select(event) } } : undefined}
    >
      {interactive && schedule !== 'active' ? <span className={styles.scheduleBadge}>{schedule === 'scheduled' ? 'Scheduled' : 'Expired'}</span> : null}
      <div className={styles.cardSlotContent}>{children}</div>
    </div>
  )
}

// FeaturedShowcaseView (FeaturedShowcase.swift): optional heading, then a paged rail or a stack of cards.
function FeaturedShowcase({ component, catalog, interactive, selectedCardId, onSelectCard, onMoveCardTo }) {
  const cards = interactive ? component.cards : activeCards(component)
  const carousel = component.layout !== 'stack'
  const [visibleIndex, setVisibleIndex] = useState(0)
  if (cards.length === 0) return interactive ? <EditorPlaceholder>Add a card to this showcase.</EditorPlaceholder> : null
  // The page dots follow the card snapped to the rail's leading edge, the way
  // the app's scrollPosition binding drives them.
  const trackVisibleCard = (event) => {
    const rail = event.currentTarget
    const origin = rail.getBoundingClientRect().left
    let nearest = 0
    let distance = Number.POSITIVE_INFINITY
    Array.from(rail.children).forEach((child, position) => {
      const gap = Math.abs(child.getBoundingClientRect().left - origin)
      if (gap < distance) { distance = gap; nearest = position }
    })
    setVisibleIndex(nearest)
  }
  const activeDot = Math.min(visibleIndex, cards.length - 1)
  return (
    <div className={styles.featured}>
      {component.title ? <div className={styles.sectionHeader}><span className={styles.sectionTitle}>{component.title}</span></div> : null}
      <div className={carousel ? styles.featuredRail : styles.featuredStack} data-testid="featured-cards" data-scroller={carousel ? 'x' : undefined} onScroll={carousel ? trackVisibleCard : undefined}>
        {cards.map((card, index) => (
          <CardSlot key={card.id} component={component} card={card} index={index} axis={carousel ? 'x' : 'y'} selected={card.id === selectedCardId} interactive={interactive} onSelect={onSelectCard} onMoveTo={onMoveCardTo}>
            <FeaturedCardBody card={card} catalog={catalog} interactive={interactive} />
          </CardSlot>
        ))}
      </div>
      {carousel && cards.length > 1 ? <div className={styles.pageDots} aria-hidden="true">{cards.map((card, index) => <i key={card.id} data-active={index === activeDot ? 'true' : 'false'} />)}</div> : null}
    </div>
  )
}
function formatBetwireOdds(odds) {
  if (odds == null) return null
  if (typeof odds === 'string') {
    const trimmed = odds.trim()
    return trimmed !== '' ? trimmed : null
  }
  if (typeof odds === 'number') {
    return odds > 0 ? `+${odds}` : String(odds)
  }
  if (typeof odds === 'object') {
    const american = odds.american
    if (american != null && american !== '') {
      if (typeof american === 'number') {
        return american > 0 ? `+${american}` : String(american)
      }
      return String(american)
    }
    const decimal = odds.decimal
    if (decimal != null && decimal !== '') {
      return String(decimal)
    }
  }
  return null
}

function BetwirePostCard({ item, authenticated }) {
  const creator = item.creator || {}
  const displayName = creator.display_name || creator.name || 'BetWire'
  const initials = displayName.split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase() || 'BW'
  const selectionSet = item.selection_set
  const summary = selectionSet?.summary
  const selections = Array.isArray(selectionSet?.selections) ? selectionSet.selections : []
  const media = Array.isArray(item.media) && item.media.length > 0 ? item.media[0] : null
  const posterURL = media?.playback?.poster_url || media?.poster_url || (media?.type === 'image' ? media.url : null)

  const entries = Array.isArray(selectionSet?.entries) ? selectionSet.entries : []
  const hasBetslipEntry = entries.some(
    (entry) => entry.action?.type === 'betslip' || (!entry.action && entry.kind === 'betslip')
  ) || (Array.isArray(item.entry_availability) && item.entry_availability.length > 0)

  const ctaLabel = hasBetslipEntry ? (authenticated ? 'Review & Copy' : 'Sign in to copy') : null
  const summaryOdds = formatBetwireOdds(summary?.odds)

  return (
    <div className={`${styles.card} ${styles.betwireCard}`}>
      <div className={styles.betwireHeader}>
        {creator.avatar_url ? (
          <img className={styles.betwireAvatar} src={creator.avatar_url} alt="" />
        ) : (
          <span className={styles.betwireInitials}>{initials}</span>
        )}
        <div className={styles.betwireCreatorMeta}>
          <strong className={styles.betwireCreatorName}>{displayName}</strong>
          {item.created_at ? (
            <span className={styles.betwireTimestamp}>
              {new Date(item.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
            </span>
          ) : null}
        </div>
      </div>
      {item.title ? <p className={styles.betwireText}><strong>{item.title}</strong></p> : null}
      {item.subtitle ? <p className={styles.betwireTimestamp}>{item.subtitle}</p> : null}
      {item.text ? <p className={styles.betwireText}>{item.text}</p> : null}
      {posterURL ? (
        <div className={styles.betwireMedia}>
          <img src={posterURL} alt="" className={styles.betwireMediaImage} />
        </div>
      ) : null}
      {summary?.label || selections.length > 0 ? (
        <div className={styles.betwireWager}>
          <span className={styles.betwireWagerLabel}>
            {summary?.label || `${selections.length} Pick${selections.length === 1 ? '' : 's'}`}
            {summaryOdds ? ` · ${summaryOdds}` : ''}
          </span>
          {selections.slice(0, 3).map((sel) => {
            const odds = formatBetwireOdds(sel.odds)
            return (
              <div key={sel.id} className={styles.betwireLeg}>
                <span>{sel.label || sel.subtitle || 'Selection'}</span>
                {odds ? <strong className={styles.betwireLegOdds}>{odds}</strong> : null}
              </div>
            )
          })}
        </div>
      ) : null}
      {ctaLabel ? (
        <div className={styles.betwireFooter}>
          <span className={styles.betwireAction}>{ctaLabel}</span>
        </div>
      ) : null}
    </div>
  )
}

function BetwireSurfaceSection({ component, interactive, slug, authenticated }) {
  const effectiveSlug = slug || ''
  const carousel = component.layout !== 'stack'
  const pageSize = Math.max(1, Math.min(10, component.page_size || 3))
  const [visibleIndex, setVisibleIndex] = useState(0)

  useEffect(() => {
    setVisibleIndex(0)
  }, [component.surface_id])

  const previewQuery = useQuery({
    queryKey: ['betwire-surface-preview', effectiveSlug, component.surface_id, pageSize],
    queryFn: ({ signal }) => nativeSportsbookApi.previewBetWireSurface(effectiveSlug, component.surface_id, pageSize, signal),
    enabled: Boolean(effectiveSlug && component.surface_id),
    staleTime: 30_000,
    retry: false,
  })

  if (!effectiveSlug) {
    return <EditorPlaceholder>Feed preview scope is unavailable.</EditorPlaceholder>
  }
  if (!component.surface_id) {
    return interactive ? <EditorPlaceholder>Select a feed surface in the inspector.</EditorPlaceholder> : null
  }

  if (previewQuery.isLoading) {
    return <div className={styles.placeholder}>Loading feed posts…</div>
  }

  if (previewQuery.isError) {
    const errorData = previewQuery.error?.response?.data
    const message = errorData?.error || previewQuery.error?.message || 'Feed posts could not be loaded.'
    return (
      <div className={styles.placeholder}>
        <div>{message}</div>
        <button type="button" className={styles.betwireAction} onClick={() => previewQuery.refetch()}>
          Retry
        </button>
      </div>
    )
  }


  const items = previewQuery.data?.items || []
  if (items.length === 0) {
    return interactive ? <EditorPlaceholder>No published posts on this surface.</EditorPlaceholder> : null
  }

  const displayItems = items.slice(0, pageSize)

  const trackVisibleCard = (event) => {
    const rail = event.currentTarget
    const origin = rail.getBoundingClientRect().left
    let nearest = 0
    let distance = Number.POSITIVE_INFINITY
    Array.from(rail.children).forEach((child, position) => {
      const gap = Math.abs(child.getBoundingClientRect().left - origin)
      if (gap < distance) { distance = gap; nearest = position }
    })
    setVisibleIndex(nearest)
  }
  const activeDot = Math.min(visibleIndex, displayItems.length - 1)

  return (
    <div className={styles.featured}>
      {component.title ? (
        <div className={styles.sectionHeader}>
          <span className={styles.sectionTitle}>{component.title}</span>
        </div>
      ) : null}
      <div
        className={carousel ? styles.featuredRail : styles.featuredStack}
        data-testid="betwire-cards"
        data-scroller={carousel ? 'x' : undefined}
        onScroll={carousel ? trackVisibleCard : undefined}
      >
        {displayItems.map((item) => (
          <div key={item.id} className={carousel ? styles.cardSlot : undefined}>
            <BetwirePostCard item={item} authenticated={authenticated} />
          </div>
        ))}
      </div>
      {carousel && displayItems.length > 1 ? (
        <div className={styles.pageDots} aria-hidden="true">
          {displayItems.map((item, index) => (
            <i key={item.id} data-active={index === activeDot ? 'true' : 'false'} />
          ))}
        </div>
      ) : null}
    </div>
  )
}

// Under live data the section asks the tenant runtime for the same provider-posted
// markets the app fetches; the fixture bundle answers only for sample games.
function useCompetitionMarkets({ slug, source, sport, name, kind, limit, enabled }) {
  const live = isLiveSource(source)
  const query = useQuery({
    queryKey: ['native-competition-markets', slug, source, sport, name, kind, limit],
    enabled: live && enabled && Boolean(slug),
    queryFn: ({ signal }) => nativeSportsbookApi.previewCompetitionMarkets(slug, { sport, competition: name, kind, limit, source }, signal),
    staleTime: 5000, refetchInterval: 15000, retry: false,
  })
  if (!live) {
    const key = sport && name ? `${sport}/${name}` : null
    return { entry: key ? competitionMarketsBundle.competitions?.[key]?.[kind] : null, source: competitionMarketsBundle.source || 'fixture' }
  }
  if (query.isError) return { entry: { state: 'unavailable' }, source: 'kambi_offering' }
  return { entry: query.data ?? { state: query.isFetching ? 'loading' : 'unavailable' }, source: query.data?.source || 'kambi_offering' }
}

function CompetitionMarketsSection({ component, catalog, slug }) {
  const kind = component.kind === 'futures' ? 'futures' : 'player_props'
  const sport = component.competition?.sport
  const name = component.competition?.name
  const limit = Math.max(1, Math.min(12, component.limit || 6))
  const rehearsal = catalog?.rehearsal

  // A competition the source does not post is unavailable, never borrowed MLB inventory.
  const { entry, source } = useCompetitionMarkets({ slug, source: catalog?.source, sport, name, kind, limit, enabled: Boolean(sport && name) && !rehearsal })
  let state = entry?.state || 'unavailable'
  if (rehearsal === 'empty') {
    state = 'empty'
  } else if (rehearsal === 'unavailable_inventory' || rehearsal === 'unavailable_markets') {
    state = 'unavailable'
  }

  if (state === 'loading') {
    return (
      <div className={styles.competitionMarketsSection}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionTitle}>{component.title}</span>
        </div>
        <div className={`${styles.card} ${styles.placeholder}`} aria-busy="true">
          <div>Loading markets…</div>
        </div>
      </div>
    )
  }

  if (state === 'unavailable') {
    return (
      <div className={styles.competitionMarketsSection}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionTitle}>{component.title}</span>
        </div>
        {source === 'fixture' ? (
          <div className={styles.fixtureNotice}>Fixture markets · Not live inventory</div>
        ) : null}
        <div className={`${styles.card} ${styles.placeholder}`}>
          <div>Markets are unavailable. Try again shortly.</div>
          <span className={styles.betwireAction} aria-hidden="true">Retry</span>
        </div>
      </div>
    )
  }

  if (state === 'empty' || !entry?.events || entry.events.length === 0) {
    const emptyCopy = kind === 'futures'
      ? 'No futures posted for this competition yet.'
      : 'No player props posted for this competition yet.'
    return (
      <div className={styles.competitionMarketsSection}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionTitle}>{component.title}</span>
        </div>
        {source === 'fixture' ? (
          <div className={styles.fixtureNotice}>Fixture markets · Not live inventory</div>
        ) : null}
        <div className={`${styles.card} ${styles.placeholder}`}>
          <div>{emptyCopy}</div>
        </div>
      </div>
    )
  }

  const events = entry.events

  return (
    <div className={styles.competitionMarketsSection}>
      <div className={styles.sectionHeader}>
        <span className={styles.sectionTitle}>{component.title}</span>
      </div>
      {source === 'fixture' ? (
        <div className={styles.fixtureNotice}>Fixture markets · Not live inventory</div>
      ) : null}
      {kind === 'futures' ? (
        events.slice(0, limit).map((event) => {
          const market = event.markets?.[0]
          const outcomes = market?.outcomes || []
          return (
            <div key={event.event_id} className={`${styles.card} ${styles.competitionEventCard}`}>
              <div className={styles.competitionEventHeader}>
                <span className={styles.competitionEventTitle}>{event.name}</span>
              </div>
              <div className={styles.futuresList}>
                {outcomes.map((outcome) => {
                  const marketSuspended = market?.status === 'SUSPENDED'
                  const outcomeSuspended = outcome.status === 'SUSPENDED'
                  const suspended = marketSuspended || outcomeSuspended
                  const open = !suspended && outcome.status === 'OPEN' && (market?.status === 'OPEN' || !market) && (outcome.odds_american != null || outcome.odds_millis != null)
                  const price = outcome.odds_american || (outcome.odds_millis != null ? americanOdds(outcome.odds_millis) : '—')
                  return (
                    <div key={outcome.id} className={styles.futuresRow}>
                      <span className={styles.futuresLabel}>{outcome.label}</span>
                      <span
                        className={styles.odds}
                        data-open={open ? 'true' : 'false'}
                        style={{ flex: '0 0 88px', minHeight: '36px' }}
                        tabIndex={open ? 0 : -1}
                        role="button"
                        aria-label={`${outcome.label} ${price}`}
                      >
                        {suspended ? <span className={styles.susp}>SUSP</span> : <span className={styles.oddsValue}>{price}</span>}
                      </span>
                    </div>
                  )
                })}
              </div>
            </div>
          )
        })
      ) : (
        events.slice(0, limit).map((event) => (
          <div key={event.event_id} className={`${styles.card} ${styles.competitionEventCard}`}>
            <div className={styles.competitionEventHeader}>
              <span className={styles.competitionEventTitle}>{event.name}</span>
            </div>
            {(event.markets || []).map((market) => {
              const playerLabel = market.player?.name ? `${market.player.name} · ${market.label}` : market.label
              return (
                <div key={market.id} className={styles.marketBlock}>
                  <div className={styles.marketHeader}>{playerLabel}</div>
                  <div className={styles.oddsStrip}>
                    {(market.outcomes || []).map((outcome) => {
                      const marketSuspended = market.status === 'SUSPENDED'
                      const outcomeSuspended = outcome.status === 'SUSPENDED'
                      const suspended = marketSuspended || outcomeSuspended
                      const open = !suspended && outcome.status === 'OPEN' && market.status === 'OPEN' && (outcome.odds_american != null || outcome.odds_millis != null)
                      const price = outcome.odds_american || (outcome.odds_millis != null ? americanOdds(outcome.odds_millis) : '—')
                      return (
                        <span
                          key={outcome.id}
                          className={styles.odds}
                          data-open={open ? 'true' : 'false'}
                          tabIndex={open ? 0 : -1}
                          role="button"
                          aria-label={`${playerLabel} ${outcome.label} ${price}`}
                        >
                          <span className={styles.oddsCaption}>{outcome.label}</span>
                          {suspended ? <span className={styles.susp}>SUSP</span> : <span className={styles.oddsValue}>{price}</span>}
                        </span>
                      )
                    })}
                  </div>
                </div>
              )
            })}
          </div>
        ))
      )}
    </div>
  )
}


// RemoteComponentView (Views.swift). Returns null where the app renders nothing.
function RemoteComponent({ component, catalog, liveEvent, selectedSportID, activeFilter, sportSection, interactive, selectedCardId, onSelectCard, onMoveCardTo, slug, authenticated }) {
  switch (component.type) {
    case 'sports_search':
      return <div className={styles.searchWidget}>
        <div className={styles.searchWidgetHeader}><span className={styles.searchWidgetIcon}><Search size={20} /></span><div><strong>{component.title}</strong><span>{component.placeholder}</span></div><ArrowUpRight size={16} /></div>
        {component.suggested_queries?.length > 0 ? <div className={styles.searchWidgetQueries}>{component.suggested_queries.map((query, index) => <span key={`${query}-${index}`}>{query}</span>)}</div> : null}
      </div>
    case 'featured_showcase':
      return <FeaturedShowcase component={component} catalog={catalog} interactive={interactive} selectedCardId={selectedCardId} onSelectCard={onSelectCard} onMoveCardTo={onMoveCardTo} />
    case 'hero':
      if (liveEvent) {
        const actionLabel = component.action?.label || (component.action?.route ? routeLabel(component.action.route) : null)
        return (
          <div className={styles.heroLive}>
            <div className={styles.heroLiveHeader}><span className={styles.heroHeadline}>{component.headline}</span>{actionLabel ? <span className={styles.heroAction}>{actionLabel}</span> : null}</div>
            <LiveShowcaseCard event={liveEvent} />
          </div>
        )
      }
      return <PromoHero component={component} />
    case 'event_list': {
      if (component.collection) {
        const result = catalog.collection_results?.[component.collection.id]
        const visibleIDs = new Set(catalog.events.map((event) => event.id))
        const events = (result?.events || []).filter((event) => visibleIDs.has(event.id) && eventMatchesManifestFilter(event, { eventIds: [event.id] }, selectedSportID, activeFilter))
        if (result?.state === 'empty' && component.collection.empty_behavior === 'hide') return interactive ? <EditorPlaceholder>No matching games. This section is hidden.</EditorPlaceholder> : null
        const message = collectionStateText(result)
        return events.length ? <EventListSection title={component.title} events={events} configuration={component} message={message} pricesAvailable={result.state !== 'cached'} /> : <div className={styles.navigationEmpty}><strong>{component.title}</strong><span>{message}</span></div>
      }
      if (selectedSportID !== ALL_SPORTS_ID) {
        if (sportSection?.anchorComponentID === component.id) return <EventListSection title={sportSection.title} events={sportSection.events} configuration={component} cardConfigurations={sportSection.cardConfigurations} />
        return interactive ? <EditorPlaceholder>Hidden while a specific sport is selected.</EditorPlaceholder> : null
      }
      const events = catalog.events.filter((event) => eventMatchesManifestFilter(event, { eventIds: component.event_ids, sportIds: component.sport_ids }, selectedSportID, activeFilter))
      if (events.length === 0) return interactive ? <EditorPlaceholder>No events match this section's scope.</EditorPlaceholder> : null
      return <EventListSection title={component.title} events={events} configuration={component} />
    }
    case 'live_game_momentum': {
      const event = resolveLiveGameEvent(component, catalog)
      if (!event || !eventMatchesQuickFilter(event, activeFilter)) {
        return interactive ? <EditorPlaceholder>No active live event matches this section.</EditorPlaceholder> : null
      }
      return (
        <div className={styles.heroLive}>
          <div className={styles.heroLiveHeader}><span className={styles.heroHeadline}>{component.title}</span></div>
          <LiveShowcaseCard event={event} />
        </div>
      )
    }
    case 'compliance_notice':
      return <ComplianceNotice component={component} />
    case 'betwire_surface':
      return <BetwireSurfaceSection component={component} interactive={interactive} slug={slug} catalog={catalog} authenticated={authenticated} />
    case 'competition_markets':
      return <CompetitionMarketsSection component={component} catalog={catalog} slug={slug} />
    case 'player_prop_board':
      return <PlayerPropBoardPreview component={component} catalog={catalog} slug={slug} interactive={interactive} />
    case 'same_game_parlay_builder':
      return <ParlayBuilderPreview component={component} catalog={catalog} slug={slug} interactive={interactive} />
    default:
      return null
  }
}

function PreviewSlot({ component, index, selected, interactive, onSelect, onMoveTo, onInsert, children }) {
  const ref = useRef(null)
  const pointer = useRef(null)
  const [edge, setEdge] = useState(null)
  const definition = componentDefinition(component.type)
  const locked = !definition?.policy?.movable
  const name = componentInstanceName(component)
  // Sections can run far taller than the phone, so a drop targets the side of
  // the hovered section's visible part and the move happens on release. The
  // compliance notice is pinned last: everything lands ahead of it.
  const edgeFor = (monitor) => (locked ? 'before' : dropEdge(ref.current, monitor, 'y', pointer.current))
  const [{ isDragging }, drag] = useDrag(() => ({
    type: HOME_COMPONENT_DRAG_TYPE,
    item: { index, id: component.id },
    canDrag: interactive && !locked,
    collect: (monitor) => ({ isDragging: monitor.isDragging() }),
  }), [interactive, locked, index, component.id])
  const [{ isOver }, drop] = useDrop(() => ({
    accept: [HOME_COMPONENT_DRAG_TYPE, HOME_LIBRARY_DRAG_TYPE],
    canDrop: (item, monitor) => interactive && !(monitor.getItemType() === HOME_COMPONENT_DRAG_TYPE && item.index === index),
    hover: (item, monitor) => {
      if (monitor.canDrop()) setEdge(edgeFor(monitor))
    },
    drop: (item, monitor) => {
      const side = edgeFor(monitor)
      if (!side) return
      if (monitor.getItemType() === HOME_LIBRARY_DRAG_TYPE) onInsert(item.type, side === 'before' ? index : index + 1)
      else onMoveTo(item.index, dropIndex(item.index, index, side))
    },
    collect: (monitor) => ({ isOver: monitor.isOver({ shallow: true }) && monitor.canDrop() }),
  }), [interactive, locked, index, onMoveTo, onInsert])
  useEffect(() => {
    if (!isOver) setEdge(null)
  }, [isOver])
  useEffect(() => {
    if (selected) revealPreviewElement(ref.current)
  }, [selected, index])
  drag(drop(ref))
  const select = () => onSelect?.(component.id)
  const trackPointer = (event) => { pointer.current = { x: event.clientX, y: event.clientY } }
  return (
    <div
      ref={ref}
      className={styles.slot}
      onDragEnter={interactive ? trackPointer : undefined}
      onDragOver={interactive ? trackPointer : undefined}
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      aria-label={interactive ? `${componentName(component)}: ${name}` : undefined}
      aria-pressed={interactive ? selected : undefined}
      data-interactive={interactive ? 'true' : 'false'}
      data-selected={selected ? 'true' : 'false'}
      data-dragging={isDragging ? 'true' : 'false'}
      data-drop-edge={isOver ? edge : undefined}
      data-container={Array.isArray(component.cards) ? 'true' : 'false'}
      onClick={interactive ? select : undefined}
      onKeyDown={interactive ? (event) => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); select() } } : undefined}
    >
      {interactive ? <span className={styles.slotLabel} aria-hidden="true">{componentName(component)}</span> : null}
      <div className={styles.slotContent}>{children}</div>
    </div>
  )
}

/**
 * The Home screen as the iOS client composes it: fixed app chrome (header, sport
 * carousel, quick filters) followed by the manifest components in order. When
 * `interactive` is set, components become selectable, draggable slots.
 */
export function NativePreview({
  manifest,
  screen = manifest.screens?.[0],
  onNavigateScreen,
  components,
  visibleIds,
  catalog = PREVIEW_CATALOG,
  device = 'ios',
  sessionState = 'anonymous',
  selectedId = null,
  selectedCardId = null,
  interactive = false,
  onSelect,
  onMoveTo,
  onInsert,
  onSelectCard,
  onMoveCardTo,
  slug = null,
  appearance,
  onAppearanceChange,
}) {
  // The page can own the light/dark choice so every preview shares it.
  const [localAppearance, setLocalAppearance] = useState(null)
  const requestedAppearance = appearance === undefined ? localAppearance : appearance
  const setRequestedAppearance = onAppearanceChange ?? setLocalAppearance
  const [selectedSportID, setSelectedSportID] = useState(ALL_SPORTS_ID)
  const [activeFilter, setActiveFilter] = useState(QUICK_FILTERS[0].id)
  const [sportItemId, setSportItemId] = useState(null)
  const [filterItemId, setFilterItemId] = useState(null)
  const theme = resolvePreviewTheme(manifest.theme, requestedAppearance)
  const authenticated = sessionState === 'authenticated'
  const visible = components.filter((component) => visibleIds.has(component.id))
  const navigation = screen?.navigation
  const nav = navigation ? resolveHomeNavigation(navigation, visible, catalog.events, sportItemId, filterItemId, catalog.collection_results) : null
  const contentCatalog = nav ? { ...catalog, events: nav.events } : catalog
  const contentSportID = nav ? ALL_SPORTS_ID : selectedSportID
  const contentFilter = nav ? '' : activeFilter
  const sportSection = selectedSportEventSection(visible, contentCatalog.events, contentSportID, contentFilter)
  const liveEvent = firstLiveEvent(contentCatalog, contentFilter)
  const complianceIndex = components.findIndex((component) => component.type === 'compliance_notice')
  const [{ isLibraryOver }, dropArea] = useDrop(() => ({
    accept: HOME_LIBRARY_DRAG_TYPE,
    canDrop: () => interactive,
    drop: (item, monitor) => {
      if (monitor.didDrop()) return
      onInsert?.(item.type, complianceIndex === -1 ? components.length : complianceIndex)
    },
    collect: (monitor) => ({ isLibraryOver: monitor.isOver({ shallow: true }) && monitor.canDrop() }),
  }), [interactive, complianceIndex, components.length, onInsert])
  const toggleAppearance = theme.supportsAppearanceToggle
    ? () => setRequestedAppearance(theme.appearance === 'dark' ? 'light' : 'dark')
    : null

  return (
    <div className={styles.device} style={{ ...previewThemeVariables(theme), ...previewFontVariables(manifest.theme?.typography, device) }} data-device={device} data-appearance={theme.appearance} data-testid="native-preview">
      <DeviceChrome />
      <AppHeader theme={theme} authenticated={authenticated} onToggleAppearance={toggleAppearance} />
      <div ref={dropArea} className={styles.scroll} data-scroller="y" data-drop-active={isLibraryOver ? 'true' : 'false'}>
        {screen?.context ? <div className={styles.hubHeader}>
          <button type="button" aria-label="Back to Sportsbook" onClick={() => onNavigateScreen?.(manifest.screens[0].id)}>‹</button>
          <div><strong>{screen.title}</strong><span>{sportDisplayName(screen.context.sport)}</span></div>
          <span className={styles.hubSearch} aria-label={`Search sportsbook · ${screen.context.competition}`}>⌕</span>
        </div> : onNavigateScreen && manifest.screens.filter((item) => item.context).length ? <div className={styles.hubEntries}>
          <div className={styles.hubLinks}>{manifest.screens.filter((item) => item.context).map((hub) => <button type="button" key={hub.id} onClick={() => onNavigateScreen(hub.id)}>{hub.title}</button>)}</div>
        </div> : null}
        {nav ? <>
          <div className={styles.managedBars}>
          <ManagedNavigation bar={navigation.sports} options={nav.sports} selected={nav.sport} label="Sports navigation" onSelect={(id) => { setSportItemId(id); setFilterItemId(null) }} onEdit={interactive ? () => onSelect?.('navigation:sports') : null} editing={selectedId === 'navigation:sports'} trailingActions />
          <ManagedNavigation bar={navigation.filters} options={nav.filters} selected={nav.filter} label="Quick filters" onSelect={setFilterItemId} onEdit={interactive ? () => onSelect?.('navigation:filters') : null} editing={selectedId === 'navigation:filters'} />
          </div>
          {!nav.events.length && nav.inventory.length > 0 ? <div className={styles.navigationEmpty}><strong>No matching games</strong><span>Try another sport or filter.</span></div> : null}
        </> : !screen?.context ? <div className={styles.navigationChrome}>
          <SportNav
            sports={catalogSports(catalog.events)}
            selected={selectedSportID}
            onSelect={(id) => {
              setSelectedSportID(id)
              // resetHomeAction (HomeView.swift): Home also restores the default filter.
              if (id === ALL_SPORTS_ID) setActiveFilter(QUICK_FILTERS[0].id)
            }}
          />
          <QuickFilters active={activeFilter} onSelect={setActiveFilter} />
        </div> : null}
        {previewInventoryMessage(catalog.rehearsal) && !visible.some((component) => component.collection)
          ? <div className={styles.navigationEmpty}><strong>{previewInventoryMessage(catalog.rehearsal)}</strong></div> : null}
        {components.map((component, index) => {
          if (!visibleIds.has(component.id)) return null
          const body = <RemoteComponent component={component} catalog={contentCatalog} liveEvent={liveEvent} selectedSportID={contentSportID} activeFilter={contentFilter} sportSection={sportSection} interactive={interactive} selectedCardId={component.id === selectedId ? selectedCardId : null} onSelectCard={onSelectCard} onMoveCardTo={onMoveCardTo} slug={slug} authenticated={authenticated} />
          if (body === null) return null
          return (
            <PreviewSlot
              key={component.id}
              component={component}
              index={index}
              selected={component.id === selectedId && !selectedCardId}
              interactive={interactive}
              onSelect={onSelect}
              onMoveTo={onMoveTo}
              onInsert={onInsert}
            >
              {body}
            </PreviewSlot>
          )
        })}
        {interactive && visible.length <= 1 ? <div className={styles.dropHint}>Drag a section from the Library here.</div> : null}
      </div>
    </div>
  )
}
